import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';

import {
  getUsersForOrganization,
  removeUsersRoleOnTheOrganization,
} from '@app/adapter/organization-service';
import { ConfirmDialog } from '@app/components/Shared/ConfirmDialog';
import { StandardStyleDataGrid } from '@app/components/Shared/StandardStyleDataGrid';
import { MemberInviteDialog } from '@app/components/Users/MemberInviteDialog';
import { Organization } from '@app/types/organization';
import { User } from '@app/types/user';
import { isError } from '@app/utils/error';
import { getRoleLabel, getUserFullName } from '@app/utils/user';
import { useSetSnackbar } from '@app/utils/useSetSnackbar';

type Props = {
  organization: Organization;
};

export function UserOrgInfo({ organization }: Props) {
  const setSnackbar = useSetSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isShowAddDialog, setIsShowAddDialog] = useState(false);
  const [isShowRemoveDialog, setIsShowRemoveDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isRemovingManagerRole, setIsRemovingManagerRole] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<undefined | User>(undefined);
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: '氏名',
      renderCell: (params) => {
        return (
          <Box className="text-ellipsis">
            {params.row.name || getUserFullName(params.row)}
          </Box>
        );
      },
      sortable: false,
      width: 210,
    },
    {
      field: 'email',
      flex: 1,
      headerName: 'メールアドレス',
      minWidth: 280,
      renderCell: (params) => (
        <Box className="text-ellipsis">{params.row.email}</Box>
      ),
      sortable: false,
    },
    {
      field: 'role',
      headerName: '権限',
      renderCell: (params) => getRoleLabel(params.row.role),
      sortable: false,
      width: 100,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuAnchorEl(e.currentTarget);
            setMenuRow(params.row);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
      sortable: false,
      width: 80,
    },
  ];

  const [rows, setRows] = useState<User[]>([]);
  const [rowTotal, setRowTotal] = useState<number>(0);

  const fetchUsersForOrganization = useCallback(async () => {
    if (!organization) return;
    try {
      setIsLoading(true);
      const response = await getUsersForOrganization(organization.id);
      setRows(response.data.value);
      setRowTotal(response.data.total);
    } catch (error) {
      if (isError(error)) {
        setSnackbar(true, error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [setSnackbar, organization]);

  const submitMemberInvite = () => {
    setIsShowAddDialog(false);
    void fetchUsersForOrganization();
  };

  const handleRemoveManagerRoleConfirm = async () => {
    if (!organization || !selectedUser) return;
    try {
      setIsRemovingManagerRole(true);
      await removeUsersRoleOnTheOrganization(organization.id, selectedUser.id);
      setIsShowRemoveDialog(false);
      setSelectedUser(null);
      setSnackbar(true, '医療機関の管理者から除外しました', 'success');
      void fetchUsersForOrganization();
    } catch (error) {
      if (isError(error)) {
        setSnackbar(true, error.message, 'error');
      }
    } finally {
      setIsRemovingManagerRole(false);
    }
  };

  const handleRemoveManagerRoleCancel = (status?: boolean) => {
    if (status) {
      void handleRemoveManagerRoleConfirm();
      return;
    }
    setIsShowRemoveDialog(false);
    setSelectedUser(null);
  };

  useEffect(() => {
    void fetchUsersForOrganization();
  }, [fetchUsersForOrganization]);

  return (
    <Stack p={2} gap={2}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h5">利用ユーザー </Typography>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => setIsShowAddDialog(true)}
        >
          ユーザー招待
        </Button>
      </Stack>

      <StandardStyleDataGrid
        hideFooter
        sx={{ width: '100%' }}
        columns={columns}
        rows={rows}
        rowCount={rowTotal}
        loading={isLoading}
        className="b-radius-top-less"
      />
      <Menu
        anchorEl={menuAnchorEl}
        open={!!menuRow}
        onClose={() => {
          setMenuAnchorEl(null);
          setMenuRow(undefined);
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuRow) {
              setSelectedUser(menuRow);
              setIsShowRemoveDialog(true);
            }
            setMenuAnchorEl(null);
            setMenuRow(undefined);
          }}
        >
          医療機関の管理者から除外
        </MenuItem>
      </Menu>
      <MemberInviteDialog
        organization={organization}
        open={isShowAddDialog}
        onClickSubmit={() => submitMemberInvite()}
        onClickCancel={() => setIsShowAddDialog(false)}
      />
      <ConfirmDialog
        open={isShowRemoveDialog}
        onClose={handleRemoveManagerRoleCancel}
        title="利用ユーザーからの除外の確認"
        okButtonText="除外する"
        okButtonLoading={isRemovingManagerRole}
      >
        <div>
          利用ユーザーを除外すると、下記のユーザーは医療機関の求人管理や応募管理などの画面にアクセスできなくなります。
          <br />
          除外してよろしいでしょうか？
        </div>
        <Typography mt={3}>
          {selectedUser && (selectedUser.name || getUserFullName(selectedUser))}
        </Typography>
        <Typography>{selectedUser?.email}</Typography>
      </ConfirmDialog>
    </Stack>
  );
}
